/**
 * SavedArticlesPage — the signed-in user's Read Later list.
 * Displays every saved article newest-saved first with the same routing
 * rules as the main news feed (external → new tab, internal → detail page).
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Bookmark, ArrowLeft, ExternalLink, Newspaper, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SaveArticleButton from "@/components/SaveArticleButton";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";

function ArticleCard({ article, onRemoved }) {
  const src = article.source_url;
  const isExternal = typeof src === "string" && /^https?:\/\//i.test(src);
  const inner = (
    <>
      <div className="aspect-[16/10] overflow-hidden bg-tbc-cream/40 relative">
        {article.image_url || article.image ? (
          <img
            src={article.image_url || article.image}
            alt={article.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Newspaper className="w-8 h-8 text-tbc-gold/70" />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <SaveArticleButton
            articleId={article.article_id}
            initialSaved={true}
            onChange={(nowSaved) => { if (!nowSaved) onRemoved?.(article.article_id); }}
          />
        </div>
        {isExternal && (
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/60 text-white">
            <ExternalLink className="w-3 h-3" /> External
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold bg-tbc-gold/20 text-tbc-navy">
          {article.category || "News"}
        </span>
        <h3 className="font-heading text-tbc-navy mt-2 line-clamp-2">{article.title}</h3>
        {article.excerpt && (
          <p className="text-xs text-tbc-navy/60 mt-1 line-clamp-2">{article.excerpt}</p>
        )}
      </div>
    </>
  );
  const commonClass = "block rounded-xl overflow-hidden bg-white border border-tbc-navy/10 hover:shadow-md transition-shadow";
  if (isExternal) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className={commonClass} data-testid={`saved-article-${article.article_id}`}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={`/news/${article.article_id}`} className={commonClass} data-testid={`saved-article-${article.article_id}`}>
      {inner}
    </Link>
  );
}

export default function SavedArticlesPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/articles/saved/mine`, { withCredentials: true });
      setArticles(r.data.articles || []);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    document.title = "Read Later — The Blk Community";
    load();
  }, [load]);

  const handleRemoved = (id) => {
    setArticles((prev) => prev.filter((a) => a.article_id !== id));
  };

  return (
    <div className="min-h-screen bg-tbc-cream flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/news")} className="text-tbc-navy/70 hover:text-tbc-navy" aria-label="Back to news" data-testid="saved-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-3xl text-tbc-navy flex items-center gap-2">
              <Bookmark className="w-6 h-6 text-tbc-gold fill-tbc-gold" strokeWidth={0} />
              Read Later
            </h1>
            <p className="text-sm text-tbc-navy/60 mt-1" data-testid="saved-count">
              {loading ? "Loading your list…" : `${articles.length} article${articles.length === 1 ? "" : "s"} saved`}
            </p>
          </div>
        </div>

        {!user && !loading && (
          <div className="bg-white rounded-2xl border border-tbc-navy/10 p-8 text-center" data-testid="saved-signed-out">
            <Bookmark className="w-8 h-8 text-tbc-gold mx-auto" />
            <h2 className="font-heading text-xl text-tbc-navy mt-3">Sign in to see your Read Later list</h2>
            <p className="text-sm text-tbc-navy/60 mt-2 max-w-md mx-auto">
              Save articles from the news feed and they&apos;ll appear here for later.
            </p>
            <Button onClick={login} className="mt-4 rounded-full bg-tbc-navy text-white hover:bg-tbc-navy/90">
              Sign in
            </Button>
          </div>
        )}

        {user && loading && (
          <div className="text-center py-16 text-tbc-navy/50">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading your saved articles…
          </div>
        )}

        {user && !loading && articles.length === 0 && (
          <div className="bg-white rounded-2xl border border-tbc-navy/10 p-8 text-center" data-testid="saved-empty">
            <Bookmark className="w-8 h-8 text-tbc-navy/40 mx-auto" />
            <h2 className="font-heading text-xl text-tbc-navy mt-3">Nothing here yet</h2>
            <p className="text-sm text-tbc-navy/60 mt-2 max-w-md mx-auto">
              Tap the bookmark icon on any article in the news feed to save it for later.
            </p>
            <Button asChild className="mt-4 rounded-full bg-tbc-navy text-white hover:bg-tbc-navy/90">
              <Link to="/news">Browse news</Link>
            </Button>
          </div>
        )}

        {user && !loading && articles.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="saved-grid">
            {articles.map((a) => (
              <ArticleCard key={a.article_id} article={a} onRemoved={handleRemoved} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
